import { useState, useEffect, useCallback } from 'react';
import { api } from '@/shared/lib/api';
import { captureError } from '@/shared/lib/monitoring';
import { playSuccessSound, playErrorSound } from '@/shared/lib/soundEffects';
import { useHapticFeedback } from '@/shared/hooks/useHapticFeedback';
import type { GrammarTopic, SprintData, SprintQuestion, SprintCompleteResult } from './grammarTypes';

interface SprintRunnerProps {
  topic: GrammarTopic;
  stage: number;
  onExit: () => void;
  onCompleted: (result: SprintCompleteResult) => void;
  onNextStage?: (nextStage: number) => void;
}

export function SprintRunner({
  topic,
  stage,
  onExit,
  onCompleted,
  onNextStage,
}: SprintRunnerProps) {
  const { notify, impact } = useHapticFeedback();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sprint, setSprint] = useState<SprintData | null>(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);

  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const [isFinished, setIsFinished] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completeResult, setCompleteResult] = useState<SprintCompleteResult | null>(null);

  // Загрузка спринта с бэкенда
  const loadSprint = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.post<SprintData>('/grammar/sprint', {
        topic_id: topic.id,
        stage,
      });
      if (!data.questions || data.questions.length === 0) {
        throw new Error('Пустой список заданий');
      }
      setSprint(data);
      setCurrentIndex(0);
      setScore(0);
      setSelectedOption(null);
      setIsAnswerSubmitted(false);
      setIsFinished(false);
    } catch (err: any) {
      captureError(err, { context: 'SprintRunner.loadSprint', topic_id: topic.id, stage });
      setError(err.message || 'Не удалось сгенерировать спринт');
    } finally {
      setLoading(false);
    }
  }, [topic.id, stage]);

  useEffect(() => {
    loadSprint();
  }, [loadSprint]);

  const currentQ: SprintQuestion | undefined = sprint?.questions[currentIndex];

  // Обработка выбора ответа
  const handleSelectOption = (opt: string) => {
    if (isAnswerSubmitted || !currentQ) return;

    setSelectedOption(opt);
    setIsAnswerSubmitted(true);

    const norm = (s: string) => s.trim().toLowerCase();
    const correct = norm(opt) === norm(currentQ.correct_answer);

    setIsCorrect(correct);

    if (correct) {
      setScore((s) => s + 1);
      playSuccessSound();
      notify('success');
    } else {
      playErrorSound();
      notify('error');
    }
  };

  // Переход к следующему вопросу или завершение
  const handleNext = async () => {
    if (!sprint) return;

    if (currentIndex + 1 < sprint.questions.length) {
      setCurrentIndex((i) => i + 1);
      setSelectedOption(null);
      setIsAnswerSubmitted(false);
      setIsCorrect(false);
    } else {
      // Спринт завершён — отправляем результат на сервер
      setIsFinished(true);
      setCompleting(true);
      try {
        const finalScore = isCorrect ? score : score; // уже подсчитан
        const res = await api.post<SprintCompleteResult>('/grammar/sprint/complete', {
          topic_id: topic.id,
          stage,
          score: finalScore,
          total: sprint.questions.length,
        });
        setCompleteResult(res);
        onCompleted(res);
        if (res.passed) {
          impact('medium');
        }
      } catch (err) {
        captureError(err, { context: 'SprintRunner.complete', topic_id: topic.id, stage });
      } finally {
        setCompleting(false);
      }
    }
  };

  // 1. Состояние загрузки
  if (loading) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 44, marginBottom: 16 }}>🤖</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--theme-text)', marginBottom: 8 }}>
          Готовим спринт «Ступень {stage}»...
        </div>
        <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)', maxWidth: 280, margin: '0 auto' }}>
          AI подбирает 10 жизненных ситуаций и проверок на ловушки для темы {topic.title}
        </div>
      </div>
    );
  }

  // 2. Ошибка загрузки
  if (error || !sprint || !currentQ) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--theme-error)', marginBottom: 16 }}>
          {error || 'Не удалось запустить спринт'}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button
            onClick={loadSprint}
            style={{
              padding: '12px 20px',
              background: 'var(--theme-primary)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 12,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Попробовать снова
          </button>
          <button
            onClick={onExit}
            style={{
              padding: '12px 20px',
              background: 'var(--theme-bg-elevated)',
              color: 'var(--theme-text)',
              border: '1px solid var(--theme-border)',
              borderRadius: 12,
              cursor: 'pointer',
            }}
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  // 3. Экран финиша спринта (Победа или Разбор)
  if (isFinished) {
    const passed = completeResult ? completeResult.passed : score >= 7;
    const percent = Math.round((score / sprint.questions.length) * 100);

    return (
      <div style={{ padding: '30px 20px', maxWidth: 460, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>
          {passed ? (stage === 3 ? '🏆' : '🎉') : '💪'}
        </div>

        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--theme-text)', margin: '0 0 8px' }}>
          {passed
            ? (stage === 3 ? 'Тема полностью освоена!' : `Ступень ${stage} успешно пройдена!`)
            : `Почти получилось!`}
        </h2>

        <div style={{ fontSize: 14, color: 'var(--theme-text-secondary)', marginBottom: 24 }}>
          {passed
            ? `Отличная работа по теме «${topic.title}»!`
            : `Для зачёта нужно минимум 7 верных ответов из 10. Давай закрепим тему ещё раз.`}
        </div>

        {/* Карточка результата */}
        <div
          style={{
            background: 'var(--theme-bg-card)',
            border: `1px solid ${passed ? 'var(--theme-success)' : 'var(--theme-warning)'}`,
            borderRadius: 18,
            padding: '20px 16px',
            marginBottom: 24,
            boxShadow: 'var(--theme-shadow)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
                Результат
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: passed ? 'var(--theme-success)' : '#f59e0b', marginTop: 4 }}>
                {score} / {sprint.questions.length}
              </div>
              <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginTop: 2 }}>
                {percent}% верно
              </div>
            </div>

            <div style={{ width: 1, height: 48, background: 'var(--theme-border)' }} />

            <div>
              <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
                Награда
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--theme-primary)', marginTop: 4 }}>
                +{completeResult?.xp_gained ?? (score * 2)} XP
              </div>
              <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 2, fontWeight: 700 }}>
                {stage === 1 && '⭐ 1-я звезда'}
                {stage === 2 && '⭐⭐ 2-я звезда'}
                {stage === 3 && '⭐⭐⭐ Мастер'}
              </div>
            </div>
          </div>

          {passed && (
            <div
              style={{
                padding: '10px 14px',
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                borderRadius: 12,
                fontSize: 13,
                color: 'var(--theme-success)',
                fontWeight: 600,
              }}
            >
              ✓ Звезда мастерства сохранена в профиле!
            </div>
          )}
        </div>

        {/* Действия */}
        <div style={{ display: 'grid', gap: 10 }}>
          {passed && stage < 3 && onNextStage && (
            <button
              onClick={() => onNextStage(stage + 1)}
              style={{
                width: '100%',
                padding: '14px',
                background: 'var(--theme-primary)',
                color: '#ffffff',
                border: 'none',
                borderRadius: 14,
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
              }}
            >
              Перейти к ступени {stage + 1} →
            </button>
          )}

          {!passed && (
            <button
              onClick={loadSprint}
              style={{
                width: '100%',
                padding: '14px',
                background: 'var(--theme-primary)',
                color: '#ffffff',
                border: 'none',
                borderRadius: 14,
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Попробовать ступень {stage} снова 🔄
            </button>
          )}

          <button
            onClick={onExit}
            style={{
              width: '100%',
              padding: '14px',
              background: 'var(--theme-bg-elevated)',
              color: 'var(--theme-text)',
              border: '1px solid var(--theme-border)',
              borderRadius: 14,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Вернуться к списку тем
          </button>
        </div>
      </div>
    );
  }

  // 4. Активный вопрос спринта
  const totalQuestions = sprint.questions.length;
  const progressPercent = ((currentIndex + 1) / totalQuestions) * 100;

  const isContrast = currentQ.type === 'contrast_choice';
  const isErrorCheck = currentQ.type === 'find_error';
  const isFill = currentQ.type === 'fill_blanks';

  const badgeConfig = isContrast
    ? { title: '⚔️ Битва времён (Что выберет носитель?)', bg: 'rgba(168, 85, 247, 0.15)', color: '#c084fc', border: 'rgba(168, 85, 247, 0.3)' }
    : isErrorCheck
      ? { title: '🔍 Найди ошибку', bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', border: 'rgba(245, 158, 11, 0.3)' }
      : isFill
        ? { title: '✏️ Вставь правильную форму', bg: 'rgba(20, 184, 166, 0.15)', color: '#2dd4bf', border: 'rgba(20, 184, 166, 0.3)' }
        : { title: '🎯 Выбери верный вариант', bg: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: 'rgba(59, 130, 246, 0.3)' };

  return (
    <div style={{ padding: '16px', maxWidth: 480, margin: '0 auto' }}>
      {/* Шапка: Кнопка ✕ + Заголовок + Счётчик */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <button
          onClick={() => {
            if (window.confirm('Выйти из спринта? Прогресс этой попытки не сохранится.')) {
              onExit();
            }
          }}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--theme-text-muted)',
            fontSize: 22,
            cursor: 'pointer',
            padding: '4px 8px',
          }}
        >
          ✕
        </button>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--theme-text)' }}>
            Ступень {stage}: {sprint.stage_title}
          </div>
          <div style={{ fontSize: 11, color: 'var(--theme-text-secondary)' }}>
            {topic.title}
          </div>
        </div>

        <div
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: 'var(--theme-primary)',
            background: 'rgba(99, 102, 241, 0.15)',
            padding: '4px 8px',
            borderRadius: 8,
          }}
        >
          {currentIndex + 1} / {totalQuestions}
        </div>
      </div>

      {/* Progress Bar */}
      <div
        style={{
          height: 6,
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: 999,
          overflow: 'hidden',
          marginBottom: 20,
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${progressPercent}%`,
            background: 'linear-gradient(90deg, #6366f1 0%, #a855f7 100%)',
            borderRadius: 999,
            transition: 'width 0.3s ease',
          }}
        />
      </div>

      {/* Карточка задания */}
      <div
        style={{
          background: 'var(--theme-bg-card)',
          border: '1px solid var(--theme-border)',
          borderRadius: 20,
          padding: '22px 18px',
          marginBottom: 16,
          boxShadow: 'var(--theme-shadow)',
        }}
      >
        {/* Бейдж типа */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 999,
            background: badgeConfig.bg,
            border: `1px solid ${badgeConfig.border}`,
            color: badgeConfig.color,
            fontSize: 11,
            fontWeight: 700,
            marginBottom: 14,
          }}
        >
          {badgeConfig.title}
        </div>

        {/* Предложение */}
        <div
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: 'var(--theme-text)',
            lineHeight: 1.5,
            marginBottom: 18,
          }}
        >
          {currentQ.sentence}
        </div>

        {/* Варианты ответов */}
        <div style={{ display: 'grid', gap: 10 }}>
          {currentQ.options?.map((opt, idx) => {
            const isSelected = selectedOption === opt;
            const isCorrectOption = opt.trim().toLowerCase() === currentQ.correct_answer.trim().toLowerCase();

            let bg = 'var(--theme-bg-elevated)';
            let borderColor = 'var(--theme-border)';
            let textColor = 'var(--theme-text)';

            if (isAnswerSubmitted) {
              if (isCorrectOption) {
                bg = 'rgba(16, 185, 129, 0.2)';
                borderColor = 'var(--theme-success)';
                textColor = '#34d399';
              } else if (isSelected && !isCorrectOption) {
                bg = 'rgba(239, 68, 68, 0.2)';
                borderColor = 'var(--theme-error)';
                textColor = '#f87171';
              } else {
                textColor = 'var(--theme-text-muted)';
              }
            }

            return (
              <button
                key={idx}
                type="button"
                onClick={() => handleSelectOption(opt)}
                disabled={isAnswerSubmitted}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: bg,
                  border: `1.5px solid ${borderColor}`,
                  borderRadius: 14,
                  color: textColor,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: isAnswerSubmitted ? 'default' : 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.15s ease',
                  boxShadow: isSelected ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
                }}
              >
                <span>{opt}</span>
                {isAnswerSubmitted && isCorrectOption && <span>✓</span>}
                {isAnswerSubmitted && isSelected && !isCorrectOption && <span>✕</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Умное объяснение ошибки / правила носителя */}
      {isAnswerSubmitted && (
        <div
          style={{
            background: isCorrect ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${isCorrect ? 'rgba(16, 185, 129, 0.35)' : 'rgba(239, 68, 68, 0.35)'}`,
            borderRadius: 16,
            padding: '14px 16px',
            marginBottom: 16,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: 14,
              color: isCorrect ? 'var(--theme-success)' : 'var(--theme-error)',
              marginBottom: 4,
            }}
          >
            {isCorrect ? '✓ Верно!' : `❌ Не совсем так (Правильно: ${currentQ.correct_answer})`}
          </div>

          <div style={{ color: 'var(--theme-text-secondary)', marginBottom: currentQ.contrast_hint ? 8 : 0 }}>
            {currentQ.explanation_ru}
          </div>

          {/* Подсказка контраста / ловушки */}
          {currentQ.contrast_hint && (
            <div
              style={{
                fontSize: 12,
                color: '#c084fc',
                background: 'rgba(168, 85, 247, 0.12)',
                border: '1px solid rgba(168, 85, 247, 0.25)',
                padding: '6px 10px',
                borderRadius: 8,
                marginTop: 6,
              }}
            >
              💡 <strong>Разница:</strong> {currentQ.contrast_hint}
            </div>
          )}
        </div>
      )}

      {/* Кнопка Далее */}
      {isAnswerSubmitted && (
        <button
          onClick={handleNext}
          style={{
            width: '100%',
            padding: '15px',
            background: 'var(--theme-primary)',
            color: '#ffffff',
            border: 'none',
            borderRadius: 14,
            fontSize: 16,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(99, 102, 241, 0.4)',
          }}
        >
          {currentIndex + 1 < totalQuestions ? 'Дальше →' : 'Завершить спринт 🏁'}
        </button>
      )}
    </div>
  );
}
